import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getCsrfHeaders } from '@/utils/csrf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [viewMode, setViewMode] = useState('standard');
  const [largeText, setLargeText] = useState(false);
  const [reduceAnimations, setReduceAnimations] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load theme settings from API on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Check if user is logged in before fetching settings
        const userData = localStorage.getItem('user');
        if (!userData || userData === 'undefined') {
          // Not logged in, use defaults from localStorage
          const savedTheme = localStorage.getItem('theme') || 'light';
          const savedViewMode = localStorage.getItem('view_mode') || 'standard';
          setTheme(savedTheme);
          setViewMode(savedViewMode);
          setIsLoaded(true);
          return;
        }

        const response = await axios.get(`${API_URL}/api/settings`, {
          withCredentials: true
        });
        
        if (response.data) {
          const settings = response.data;
          setTheme(settings.theme || 'light');
          setViewMode(settings.view_mode || 'standard');
          setLargeText(settings.large_text || false);
          setReduceAnimations(settings.reduce_animations || false);
          setHighContrast(settings.high_contrast || false);
        }
      } catch (error) {
        // If not logged in or error, use defaults from localStorage
        const savedTheme = localStorage.getItem('theme') || 'light';
        const savedViewMode = localStorage.getItem('view_mode') || 'standard';
        setTheme(savedTheme);
        setViewMode(savedViewMode);
      } finally {
        setIsLoaded(true);
      }
    };

    loadSettings();
  }, []);

  // Apply theme to document
  useEffect(() => {
    if (!isLoaded) return;

    const root = document.documentElement;
    
    // Apply dark/light theme
    if (theme === 'dark') {
      root.classList.add('dark');
      document.body.style.backgroundColor = 'hsl(222.2, 84%, 4.9%)';
      document.body.style.color = 'hsl(210, 40%, 98%)';
    } else {
      root.classList.remove('dark');
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
    }

    // Save to localStorage for persistence
    localStorage.setItem('theme', theme);
  }, [theme, isLoaded]);

  // Apply view mode
  useEffect(() => {
    if (!isLoaded) return;

    const root = document.documentElement;
    
    if (viewMode === 'compact') {
      root.classList.add('compact-view');
      // Apply compact styles
      root.style.setProperty('--spacing-multiplier', '0.75');
    } else {
      root.classList.remove('compact-view');
      root.style.setProperty('--spacing-multiplier', '1');
    }

    localStorage.setItem('view_mode', viewMode);
  }, [viewMode, isLoaded]);

  // Apply large text mode
  useEffect(() => {
    if (!isLoaded) return;

    const root = document.documentElement;
    
    if (largeText) {
      root.classList.add('large-text');
      root.style.fontSize = '18px';
    } else {
      root.classList.remove('large-text');
      root.style.fontSize = '';
    }
  }, [largeText, isLoaded]);

  // Apply reduce animations
  useEffect(() => {
    if (!isLoaded) return;

    const root = document.documentElement;
    
    if (reduceAnimations) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }
  }, [reduceAnimations, isLoaded]);

  // Apply high contrast
  useEffect(() => {
    if (!isLoaded) return;

    const root = document.documentElement;
    
    if (highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
  }, [highContrast, isLoaded]);

  const updateTheme = async (newTheme) => {
    setTheme(newTheme);
    
    // Also save to backend
    try {
      const csrfHeaders = getCsrfHeaders();
      await axios.put(`${API_URL}/api/settings`, { theme: newTheme }, {
        withCredentials: true,
        headers: csrfHeaders
      });
    } catch (error) {
      console.error('Failed to save theme to backend:', error);
    }
  };

  const updateViewMode = async (newViewMode) => {
    setViewMode(newViewMode);
    
    try {
      const csrfHeaders = getCsrfHeaders();
      await axios.put(`${API_URL}/api/settings`, { view_mode: newViewMode }, {
        withCredentials: true,
        headers: csrfHeaders
      });
    } catch (error) {
      console.error('Failed to save view mode to backend:', error);
    }
  };

  const updateLargeText = async (enabled) => {
    setLargeText(enabled);
    
    try {
      const csrfHeaders = getCsrfHeaders();
      await axios.put(`${API_URL}/api/settings`, { large_text: enabled }, {
        withCredentials: true,
        headers: csrfHeaders
      });
    } catch (error) {
      console.error('Failed to save large text setting:', error);
    }
  };

  const updateReduceAnimations = async (enabled) => {
    setReduceAnimations(enabled);
    
    try {
      const csrfHeaders = getCsrfHeaders();
      await axios.put(`${API_URL}/api/settings`, { reduce_animations: enabled }, {
        withCredentials: true,
        headers: csrfHeaders
      });
    } catch (error) {
      console.error('Failed to save reduce animations setting:', error);
    }
  };

  const updateHighContrast = async (enabled) => {
    setHighContrast(enabled);
    
    try {
      const csrfHeaders = getCsrfHeaders();
      await axios.put(`${API_URL}/api/settings`, { high_contrast: enabled }, {
        withCredentials: true,
        headers: csrfHeaders
      });
    } catch (error) {
      console.error('Failed to save high contrast setting:', error);
    }
  };

  const value = {
    theme,
    viewMode,
    largeText,
    reduceAnimations,
    highContrast,
    isLoaded,
    setTheme: updateTheme,
    setViewMode: updateViewMode,
    setLargeText: updateLargeText,
    setReduceAnimations: updateReduceAnimations,
    setHighContrast: updateHighContrast,
    isDark: theme === 'dark',
    isCompact: viewMode === 'compact'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
